import { Empty, Spin } from 'antd';
import moment from 'moment';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Label,
    ResponsiveContainer,
    Tooltip,
    TooltipProps,
    XAxis,
    YAxis,
} from 'recharts';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import { RootState } from '../../../../../../store/rootReducer';
import { commaSeperator } from '../../../../../utils/validation';
import { distributionType, getDistributeXAxisName, getRandomColor } from '../Performances/Utils';

const formatter = (value: number) => `${commaSeperator(String(value))}`;

const InvestmentDistribution: React.FC = () => {
    const loading = useSelector((state: RootState) => state.investments.distributionLoading);
    const distributions = useSelector((state: RootState) => state.investments.distributions);
    const type = useSelector(
        (state: RootState) => state.investments.investmentDetails?.deal?.info.summary?.terms?.distribution,
    );
    const currency = useMemo(() => {
        if (distributions && distributions?.distributions && distributions?.distributions.length > 0) {
            return distributions.distributions[0].amount?.currency || '';
        }
        return '';
    }, [distributions]);

    const data = useMemo(() => {
        const list: {
            name: string;
            value?: number;
            year?: number;
            month?: number;
        }[] = [];
        distributions?.distributions?.forEach((d) => {
            list.push({
                name: `${distributionType(type, d.month)} ${d.year}`,
                value: d.amount?.amountUpto2dp,
                year: d.year,
                month: d.month,
            });
        });
        return list;
    }, [distributions, type]);

    return (
        <div className="pb-4 px-2 md:px-6 font-normal text-sm">
            {!loading && data.length > 0 && (
                <>
                    <ResponsiveContainer width="100%" height={400}>
                        <BarChart data={data} margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                            <CartesianGrid stroke="#ededed" />
                            <Tooltip content={<CustomTooltip />} cursor={{ fill: '#ffffff', opacity: 0.5 }} />
                            <XAxis
                                dataKey="name"
                                stroke="#ededed"
                                tick={{ fill: '#000000', fontSize: '0.8rem' }}
                                label={{
                                    value: `${getDistributeXAxisName(type)}`,
                                    position: 'insideBottom',
                                    dy: 20,
                                }}
                            />
                            <YAxis
                                stroke="#ededed"
                                tickFormatter={formatter}
                                tick={{ fill: '#000000', fontSize: '0.7rem' }}
                            >
                                <Label
                                    value={`Distribution (${currency})`}
                                    position="insideLeft"
                                    angle={-90}
                                    offset={-10}
                                    style={{ textAnchor: 'middle', right: 10 }}
                                />
                            </YAxis>
                            <defs>
                                {data.map((item, index) => (
                                    <linearGradient
                                        id={`colorUv_${index}`}
                                        x1="0"
                                        y1="0"
                                        x2="0"
                                        y2="1"
                                        key={`linearGradient_${index}_${item.month}_${item.year}`}
                                    >
                                        <stop
                                            offset="0%"
                                            stopColor={getRandomColor((item.year || 0) + (item.month || 0))}
                                            stopOpacity={0.9}
                                        />
                                        <stop
                                            offset="100%"
                                            stopColor={getRandomColor((item.year || 0) + (item.month || 0))}
                                            stopOpacity={0}
                                        />
                                    </linearGradient>
                                ))}
                            </defs>
                            <Bar dataKey="value" barSize={14} fill="#ffffff">
                                {data.map((item, index) => (
                                    <Cell fill={`url(#colorUv_${index})`} key={`barchart_key_${index}_${item.year}`} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                    {distributions?.lastUpdatedOn && data.length > 0 && (
                        <span className="text-xs">
                            Data is accurate as of {moment(distributions?.lastUpdatedOn).format('MMM DD YYYY')}
                        </span>
                    )}
                    {data.length > 0 && (
                        <div>
                            <span className="text-xs">
                                Disclaimer:The quarterly dividend amount displayed is calculated based on your total
                                holdings in the Fund. The dividend amount shown is calculated based on your total
                                holdings in the Fund as at the end of each calendar quarter.
                            </span>
                        </div>
                    )}
                </>
            )}
            {!loading && data.length === 0 && (
                <div className="flex justify-center items-center h-40">
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={<p className="text-lg WorkSans font-semibold text-gray-500">No data available</p>}
                    />
                </div>
            )}
            {loading && (
                <div className="flex justify-center items-center h-40">
                    <Spin />
                </div>
            )}
        </div>
    );
};

export default InvestmentDistribution;

const CustomTooltip = ({ active, payload, label }: TooltipProps<ValueType, NameType>) => {
    if (active) {
        return (
            <div className="shadow rounded bg-white p-4">
                <p className="label font-semibold">{label}</p>
                <div className="desc">
                    {payload?.map((p, index) => (
                        <div key={`${index}_${p.name}`}>{commaSeperator(parseFloat(String(p.value)).toFixed(2))}</div>
                    ))}
                </div>
            </div>
        );
    }

    return null;
};
